import { useState } from "react";
import Spinner from "../../../ui/spinner/Spinner";
import styles from "../LoginStyles.module.css";
import SecretQuestionsForm from "./SecretQuestionsForm";
import ResetPasswordForm from "./ResetPasswordForm";
import {
  isValidUidByType,
  formatUidByType,
  normalizeSecretString,
  isValidSecretString,
  isValidPassword,
} from "../../../../auth/userCredentialsUtils";
import axios from "axios";

const PasswordRecoveryBase = ({
  type, // "username" | "email" | "phone"
  label,
  inputId,
  inputPlaceholder,
  inputMaxLength,
  emptyInputMessage,
  invalidUidMessage,
  onCancel,
  onCompletion,
}) => {
  const [loading, setLoading] = useState(false);

  const [stage, setStage] = useState("input"); // input | secrets | reset

  const [uid, setUid] = useState("");
  const [uidError, setUidError] = useState(false);

  const [secretQuestion1, setSecretQuestion1] = useState("");
  const [secretQuestion2, setSecretQuestion2] = useState("");

  const [secretAnswer1, setSecretAnswer1] = useState("");
  const [secretAnswer2, setSecretAnswer2] = useState("");
  const [error1, setError1] = useState(false);
  const [error2, setError2] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [repeatError, setRepeatError] = useState(false);

  // --------------------
  // STEP 1: UID VALIDATION + FETCH QUESTIONS
  // --------------------
  const handleUidSubmit = async () => {
    if (!uid) {
      alert(emptyInputMessage);
      return;
    }

    if (!isValidUidByType(type, uid)) {
      setUidError(true);
      return;
    }

    try {
      setLoading(true);

      const formattedUid = formatUidByType(type, uid);

      const payload = { uid: formattedUid };

      const response = await axios.post(
        "php/sign_in/get_user_secrets_using_uid.php", payload, {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      console.log("PASSWORD HELP GET SECRETS RESPONSE", response);
      console.log("PASSWORD HELP GET SECRETS DATA", response.data);

      if (data.code !== 200) {
        alert(data.message || "Failed to retrieve secret questions. Please retry.");
        return;
      }

      const questions = response.data.questions;
      setSecretQuestion1(questions.q1);
      setSecretQuestion2(questions.q2);      

      if (!questions.q1 || !questions.q2) {
        alert(
          "We cannot help you recover your password because you do not have saved questions and answers\nUse anyone of the following options:\n1. Sign in using your Google, Microsoft or Facebook account\n2. Signup as a fresh user (using username, email or phone number) and set up your password help\nPlease cancel or refresh this page"
        );
        return
      }

      setStage("secrets");

    } catch (error) {
      alert(error.message || invalidUidMessage);

    } finally {
      setLoading(false);
    }
  };

  // --------------------
  // STEP 2: SECRET ANSWERS VALIDATION
  // --------------------
  const handleAnswers = async () => {
    const ans1 = normalizeSecretString(secretAnswer1);
    const ans2 = normalizeSecretString(secretAnswer2);

    setError1(false);
    setError2(false);

    if (!isValidSecretString(ans1)) {
      setError1(true);
      return;
    }

    if (!isValidSecretString(ans2)) {
      setError2(true);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        uid: formatUidByType(type, uid),
        secretAnswer1: ans1.toLowerCase(),
        secretAnswer2: ans2.toLowerCase(),
      };

      const response = await axios.post(
        "php/sign_in/check_secret_answers.php", payload, {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      console.log("PASSWORD HELP CHECK ANSWERS RAW RESPONSE", response);
      console.log("PASSWORD HELP CHECK ANSWERS DATA", response.data);

      if (data.code !== 200) {
        alert(
          data.message || "Failed to authenticate answers. Please retry.",
        );
        return;
      }

      setStage("reset");

    } catch (err) {

      alert(err.message);

    } finally {
      setLoading(false);
    }
  };

  // --------------------
  // STEP 3: PASSWORD RESET
  // --------------------
  const handleReset = async () => {
    setPasswordError(false);
    setRepeatError(false);

    if (!isValidPassword(newPassword)) {
      setPasswordError(true);
      return;
    }

    if (newPassword !== repeatPassword) {
      setRepeatError(true);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        uid: formatUidByType(type, uid),
        password: newPassword,
      };

      const response = await axios.post(
        "php/sign_in/reset_password.php", payload, {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      console.log("PASSWORD HELP RAW RESPONSE", response);
      console.log(" PASSWORD HELP DATA", response.data);

      if (data.code !== 200) {
        alert(data.message || "Failed to reset password. Please retry.");
        return;
      }

      alert(
        "Your password has been reset. You may now sign in using the new password"
      );
      
      onCompletion();
      
    } catch (error) {

      console.log("RESET ERROR OBJECT:", error);
      alert(error.message);

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.classes}>
      <div className={styles.floatRight}>
        <button className={styles.bgRed} onClick={onCancel}>
          Cancel
        </button>
      </div>

      <header className={styles.hdrMT}>
        <h2>Recover your password</h2>
      </header>

      {/* STEP 1 */}
      {stage === "input" && (
        <>
          <div className={styles.inputView}>
            <label htmlFor={inputId}>{label}</label>
            <input
              id={inputId}
              placeholder={inputPlaceholder}
              maxLength={inputMaxLength}
              value={uid}
              onChange={(e) => {
                setUidError(false);
                setUid(e.target.value);
              }}
            />
            {uidError && (
              <span className={styles.errorText}>{invalidUidMessage}</span>
            )}
          </div>

          <button
            className={`${styles.bgGreen} ${styles.btnTBMargins}`}
            onClick={handleUidSubmit}
          >
            Submit
          </button>
        </>
      )}

      {/* STEP 2 */}
      {stage === "secrets" && (
        <SecretQuestionsForm
          secretQuestion1={secretQuestion1}
          secretQuestion2={secretQuestion2}
          secretAnswer1={secretAnswer1}
          secretAnswer2={secretAnswer2}
          setSecretAnswer1={setSecretAnswer1}
          setSecretAnswer2={setSecretAnswer2}
          secretAnswer1HasError={error1}
          secretAnswer2HasError={error2}
          answersHandler={handleAnswers}
        />
      )}

      {/* STEP 3 */}
      {stage === "reset" && (
        <ResetPasswordForm
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          repeatNewPassword={repeatPassword}
          setRepeatNewPassword={setRepeatPassword}
          newPasswordHasError={passwordError}
          repeatNewPasswordHasError={repeatError}
          newPasswordHandler={handleReset}
        />
      )}

      {loading && <Spinner />}
    </div>
  );
};

export default PasswordRecoveryBase;
